import type React from "react";
import {
  composeRenderProps,
  Radio as RACRadio,
  RadioGroup as RACRadioGroup,
  type RadioGroupProps as RACRadioGroupProps,
  type RadioProps as RACRadioProps,
} from "react-aria-components";
import { tv } from "tailwind-variants";

import { DescriptionProvider, useDescription } from "@/components/ui/field";

const radioGroupStyles = tv({
  base: "group flex",
  variants: {
    orientation: {
      horizontal:
        "flex-wrap gap-2 *:data-[ui=label]:w-full *:data-[ui=label]:text-nowrap *:[label]:mr-1",
      vertical: "flex-col gap-y-2",
    },
  },
});

export function RadioGroup(props: RACRadioGroupProps) {
  return (
    <RACRadioGroup
      {...props}
      className={composeRenderProps(props.className, (className, renderProps) =>
        radioGroupStyles({ className, ...renderProps }),
      )}
    />
  );
}

const radioFieldStyles = tv({
  base: [
    "group flex flex-col gap-y-1",
    "*:slot-description:ps-6",
    "has-[label[data-disabled]]:*:slot-description:opacity-50",
    "has-slot-description:*:[label]:font-medium",
  ],
});

export function RadioField({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <DescriptionProvider>
      <div
        {...props}
        data-ui="field"
        className={radioFieldStyles({ className })}
      />
    </DescriptionProvider>
  );
}

const radioStyles = tv({
  base: [
    "group relative flex items-center text-sm",
    "group-aria-[orientation=horizontal]:text-nowrap",
  ],
  slots: {
    indicator: [
      "grid size-[1.0625rem] shrink-0 place-content-center rounded-full mr-1.5",
      "border border-input",
    ],
    inner: "rounded-full",
  },
  variants: {
    isDisabled: { true: "opacity-50" },
    isReadOnly: { true: { indicator: "opacity-50" } },
    isInvalid: {
      true: { indicator: "border-destructive" },
    },
    isSelected: {
      true: {
        indicator: "border-primary bg-primary",
        inner: "size-1.5 bg-background",
      },
    },
    isFocusVisible: {
      true: { indicator: "outline-ring/70 outline outline-2 outline-offset-2" },
    },
  },
});

const { base, indicator, inner } = radioStyles();

export function Radio(props: RACRadioProps) {
  const descriptionContext = useDescription();

  return (
    <RACRadio
      {...props}
      aria-describedby={descriptionContext?.["aria-describedby"]}
      className={composeRenderProps(props.className, (className, renderProps) =>
        base({ className, ...renderProps }),
      )}
    >
      {(renderProps) => {
        return (
          <>
            <div slot="radio" className={indicator({ ...renderProps })}>
              <div className={inner({ ...renderProps })} />
            </div>

            {typeof props.children === "function"
              ? props.children(renderProps)
              : props.children}
          </>
        );
      }}
    </RACRadio>
  );
}
